import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { UIRouter } from '@uirouter/angular';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ClientServicesModule } from '../../Client-Services/client-services.module';
import { ActivityPanelComponent } from '../../Common/Activity/activityPanel.component';
import { AssessmentTaxesDetailsIconComponent } from '../../Common/Assessment-Taxes-Details/assessment-taxes-details-icon.component';
import { ContractTermsPanelSiteComponent } from '../../Common/Contracts-Invoices/contract-terms-panel-site.component';
import { CurrentEntityService } from '../../Common/Entity/CurrentEntityService';
import { InstanceRights, RestrictService, Roles } from '../../Common/Permissions/restrict.service';
import { EntityTypeIds, EntityTypeIDs, RyanInstanceId } from '../../constants.new';
import { ContactModule } from '../../Contact/contact.module';
import { ProcessingModule } from '../../Processing/processing.module';
import { PropertyCharacteristicsModule } from '../../Property-Characteristics/propertyCharacteristics.module';
import { AceLoaderComponent } from '../../UI-Lib/ACE/Loader/loader.component';
import { BreadCrumb } from '../../UI-Lib/Bread-Crumb/breadCrumbs.component';
import { UILibModule } from '../../UI-Lib/UILib.module';
import { UserInstanceService } from '../../User/userInstance.service';
import { CompanyService } from '../Company/company.service';
import { CompanyEntity } from '../entity.model';
import { EntityService } from '../entity.service';
import { InstanceRepository } from '../Instance/instance.repository';
import { ParcelService } from '../Parcel/parcel.service.upgrade';
import { AssetListPanelComponent } from './Assets/assetListPanel.component';
import { ResponsibleEntityPanelComponent } from './Responsible-Entity/responsibleEntityPanel.component';
import { SiteModule } from './site.module';
import { SiteService } from './Site.Service.upgrade';
import { ValuationPanelComponent } from './Valuation/valuationPanel.component';
import { ReferralsPanelComponent } from 'src/app/Litigation/Referral/Referrals-Panel/referralsPanel.component';

@Component({
    selector: 'site',
    templateUrl: './site.component.html',
    imports: [
        CommonModule,
        AceLoaderComponent,
        UILibModule,
        ProcessingModule,
        ClientServicesModule,
        PropertyCharacteristicsModule,
        SiteModule,
        AssessmentTaxesDetailsIconComponent,
        ActivityPanelComponent,
        AssetListPanelComponent,
        ResponsibleEntityPanelComponent,
        ValuationPanelComponent,
        ContractTermsPanelSiteComponent,
        ContactModule,
        ReferralsPanelComponent,
    ],
})
export class SiteComponent implements OnInit {
    constructor(
        private readonly _siteService: SiteService,
        private readonly _restrictService: RestrictService,
        private readonly _userInstanceService: UserInstanceService,
        private readonly _instanceRepository: InstanceRepository,
        private readonly _currentEntityService: CurrentEntityService,
        private readonly _companyService: CompanyService,
        private readonly _parcelService: ParcelService,
        private readonly _entityService: EntityService,
        private readonly _uiRouter: UIRouter,
    ) {}

    siteId: number;
    companyId: number;
    companyName: string;
    showComplianceInfo: boolean;
    enableAllocations: boolean;
    site = undefined;
    panelMaximized: string;
    entity: CompanyEntity;
    hasRyanPrivateItemsEdit: boolean;
    hasRyanPrivateItemsView: boolean;
    hasPrivateItemsPermission: boolean;
    topLevelCompanyId: number;
    parcelYears: number[];

    breadcrumbs: BreadCrumb[] = [];

    private _destroy$: Subject<void> = new Subject();

    maximizePanel = (panel) => {
        this.panelMaximized = panel;
    };
    minimizePanel = () => {
        this.panelMaximized = null;
    };

    async ngOnInit(): Promise<void> {
        const stateParams = this._uiRouter.globals.params;
        this.siteId = parseInt(stateParams.siteId);
        this.companyId = parseInt(stateParams.companyId);

        const companyIDErrMsg = 'Company ID does not match Site';
        const notFoundErrMsg = 'Site not found';
        try {
            const siteResult = await this._siteService.load(this.siteId, this.companyId, false, true);
            if (siteResult.companyID != this.companyId) {
                throw new Error(companyIDErrMsg);
            }
            this.site = siteResult;

            this.hasPrivateItemsPermission =
                (this._restrictService.isInRole(Roles.RYANPRIVATEITEMSVIEW) ||
                    this._restrictService.isInRole(Roles.RYANPRIVATEITEMSEDIT)) &&
                this._userInstanceService.getSelectedInstance().instanceId === RyanInstanceId;

            const restrictionData = {
                isCompany: false,
                entityId: this.site.siteID,
                flag: Core.AccessRightsEnum.Write,
            };

            const hasWritePermission = await this._restrictService.hasPermission(restrictionData);

            this.entity = {
                type: 'site',
                typeId: EntityTypeIDs.SITE,
                id: this.site.siteID,
                name: this.site.name,
                canEditCompanySetups: this.site.canEditCompanySetups,
                hasWritePermission: hasWritePermission,
                isInactive: this.site.activityStatusID === 0,
            };

            this.hasRyanPrivateItemsEdit = this._restrictService.isInRole(Roles.RYANPRIVATEITEMSEDIT);

            this._currentEntityService.set(this.site, EntityTypeIds.SITE);
        } catch (error) {
            if (error.status === 404) {
                throw new Error(notFoundErrMsg);
            }
        }

        const companyResult = await this._companyService.load(this.companyId, false, false);

        this.companyName = companyResult.companyName;
        this.topLevelCompanyId = companyResult.topLevelCompanyId;
        this.showComplianceInfo =
            companyResult.ppReturnPreparationAllowed &&
            this._restrictService.hasInstanceRight(InstanceRights.COMPLIANCEFEATURESVIEW, companyResult.instanceID);
        this.enableAllocations = companyResult.enableAllocations;

        const instanceId = await this._instanceRepository.getEntityInstanceId('company', this.companyId).toPromise();

        this.hasRyanPrivateItemsView =
            this._restrictService.hasInstanceRight(InstanceRights.PRIVATEITEMSVIEW, instanceId) ||
            this._restrictService.hasInstanceRight(InstanceRights.PRIVATEITEMSEDIT, instanceId);

        this.parcelYears = (await this._parcelService.getAvailableYears(this.siteId)) || [];

        this.breadcrumbs = [
            {
                name: this.companyName,
                target: 'company',
                options: { companyId: this.companyId },
            },
        ];

        this._entityService.maximizeChange$.pipe(takeUntil(this._destroy$)).subscribe((changeData) => {
            if (changeData) {
                this.panelMaximized = changeData.maximized ? changeData.panelName : null;
            }
        });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    get showReferralsPanel(): boolean {
        return (
            this.hasPrivateItemsPermission &&
            (this.site.litigationReferralsEnabled || this.site.showLitigationReferrals) //if referrals are enabled for the site's company or there are existing referrals, show the panel
        );
    }
}
